import React, { useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LandingPageContext } from '../../contexts/LandingPageContext';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './ContactSection.css';

const ContactSection = () => {
    const { t } = useTranslation();
    const { contactSection } = useContext(LandingPageContext);
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [subscribers, setSubscribers] = useState([]);

    // Contact form state
    const formRef = useRef();
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [contactStatus, setContactStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubscribe = (e) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setMessage(t('contactSection.newsletterError'));
            return;
        }
        if (!subscribers.includes(email)) {
            setSubscribers([...subscribers, email]);
        }
        setMessage(t('contactSection.newsletterSuccess'));
        setEmail('');
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setContactStatus('');

        try {
            // EmailJS credentials from environment variables
            await emailjs.sendForm(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                formRef.current,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            setContactStatus('success');
            setContactForm({ name: '', email: '', message: '' });
        } catch (error) {
            console.error('EmailJS error:', error);
            setContactStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div id="contact" className="contact-section">
            <div className="contact-content">
                {/* Left Column: Title + Contact Info */}
                <div className="contact-left">
                    <div className="contact-title">
                        <div className="execution-text">{t('contactSection.executionText')}</div>
                        <div className="kontakt-letters">
                            {t('contactSection.kontakt').split('').map((letter, index) => (
                                <span key={index}>{letter}</span>
                            ))}
                        </div>
                    </div>
                    <div className="contact-details">
                        <p>{t('contactSection.address')}</p>
                        <p>{t('contactSection.phone')}</p>
                        {/* Render email as clickable mailto: link. Prefer context contactSection.email if present, else translation */}
                        {(() => {
                            const raw = (contactSection && contactSection.email) ? contactSection.email : t('contactSection.email');
                            if (!raw) return null;
                            // Find an email address in the string
                            const emailRegex = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i;
                            const match = raw.match(emailRegex);
                            if (!match) return <p>{raw}</p>;
                            const email = match[0];
                            const parts = raw.split(emailRegex);
                            // parts will be [before, email, after]
                            return (
                                <p>
                                    {parts[0]}
                                    <a href={`mailto:${email}`} className="contact-email">{email}</a>
                                    {parts[2]}
                                </p>
                            );
                        })()}
                        <div className="social-icons">
                            <a href="https://www.instagram.com/tafelberg_nuernberg/" aria-label="Instagram">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                            </a>
                        </div>
                        <Link className="impressum-link" to="/impressum">{t('contactSection.impressumLink')}</Link>
                    </div>
                    <div className="newsletter-section">
                        <h3>{t('contactSection.newsletterTitle')}</h3>
                        <form onSubmit={handleSubscribe} className="newsletter-form">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('contactSection.newsletterPlaceholder')}
                                required
                            />
                            <button type="submit">{t('contactSection.newsletterButton')}</button>
                        </form>
                        {message && <p className="newsletter-message">{message}</p>}
                    </div>
                </div>

                {/* Right Column: Contact Form */}
                <div className="contact-form-section">
                    <h3>{t('contactSection.formTitle')}</h3>
                    <form ref={formRef} onSubmit={handleContactSubmit} className="contact-form">
                        <input
                            type="text"
                            name="name"
                            value={contactForm.name}
                            onChange={handleContactChange}
                            placeholder={t('contactSection.formName')}
                            required
                        />
                        <input
                            type="email"
                            name="email"
                            value={contactForm.email}
                            onChange={handleContactChange}
                            placeholder={t('contactSection.formEmail')}
                            required
                        />
                        <textarea
                            name="message"
                            value={contactForm.message}
                            onChange={handleContactChange}
                            placeholder={t('contactSection.formMessage')}
                            rows="5"
                            required
                        />
                        <button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? t('contactSection.formSending') : t('contactSection.formSend')}
                        </button>
                    </form>
                    {contactStatus === 'success' && (
                        <p className="contact-status success">{t('contactSection.formSuccess')}</p>
                    )}
                    {contactStatus === 'error' && (
                        <p className="contact-status error">{t('contactSection.formError')}</p>
                    )}
                    <Link to="/contact" className="full-contact-link">
                        {t('contactSection.fullContactLink')} →
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ContactSection;
